import {
  generateCreatorShopSearchUrl,
  generateSearchUrl,
} from '@services/MarppleShop/features/Search/const/url';
import { SearchApiS } from '@services/MarppleShop/features/Search/outbound/share';
import { SearchModuleCreatorShopData } from '@services/MarppleShop/features/Search/type';
import { AutoComplete } from '@services/MarppleShop/features/Search/type/autocomplete.ts';
import {
  InputSearch,
  SearchArrowKeydownEvent,
  SearchDebounceInputEvent,
  SearchInputEvent,
  SearchSubmitEvent,
} from '@services/MarppleShop/renderApp/components/atoms/InputSearch/InputSearch.ts';
import { htmlIf } from '@services/MarppleShop/shared/util';
import axios from 'axios';
import { $, CustomEventWithDetail, html, View } from 'rune-ts';
import { CloseIcon } from '../../atoms/Icon';
import { AutoCompleteController } from './controllers/AutoCompleteController.ts';
import { SearchModuleController } from './controllers/SearchModuleController.ts';
import klass from './SearchModuleCreatorShop.module.scss';
import { AutoCompleteClickEvent, AutoCompleteList, AutoCompleteListOption } from './view/AutoCompleteList';
import { RecentSearchClickEvent, RecentSearchesSection } from './view/RecentSearchesSection';
import util_klass from './view/SearchModuleUtil.module.scss';
import { postSearchLogApi } from '@services/MarppleShop/features/Search/api';

type SearchModuleCreatorShopOption = Pick<AutoCompleteListOption, 'highlight'> & {
  klass?: string;
  without_visible_interaction?: boolean;
  is_mobile: boolean;
  search_input_left_part?: View;

  /**
   * @default 'ALL'
   * 자동완성 필터를 설정합니다.
   * CREATOR_SHOP 설정 및 store_id 가 존재 시 해당 스토어를 기준으로 자동 완성 데이터를 가져옵니다.
   * */
  search_type?: 'CREATOR_SHOP' | 'ALL';
  store_id: number;
  store_domain_name: string;
};

export class SearchModuleCreatorShopCloseEvent extends CustomEventWithDetail<string> {}

export class SearchModuleCreatorShop extends View<SearchModuleCreatorShopData> {
  InputSearchView: InputSearch;
  AutoCompleteListView: AutoCompleteList;
  AutoCompleteController: AutoCompleteController;
  RecentSearchesSectionView: RecentSearchesSection;
  SearchModuleController: SearchModuleController;

  constructor(
    data: SearchModuleCreatorShopData,
    private option: SearchModuleCreatorShopOption,
  ) {
    super(data, option);
    const is_mobile = this.option.is_mobile;

    this.InputSearchView = new InputSearch({
      value: this.data.value,
      placeholder: this.data.placeholder,
      klass: !is_mobile ? klass.search_input : undefined,
      debounce_ms: 200,
      transparent: !option.is_mobile,
      off_reset: !option.is_mobile,
      placeholder_gray50: true,
      left_part: option.search_input_left_part,
    });

    this.AutoCompleteListView = new AutoCompleteList({
      klass: {
        list: klass.auto_complete_list,
        item: klass.auto_complete_item,
      },
      option: {
        highlight: option.highlight,
      },
    });

    this.AutoCompleteController = new AutoCompleteController(
      {
        search: data.value,
      },
      {
        onRequestAutoComplete: this.#onRequestAutoComplete.bind(this),
      },
      {
        minCharsForAutoComplete: 2,
        customCondition(_, alreadySubmitted) {
          return !alreadySubmitted;
        },
      },
    );

    this.RecentSearchesSectionView = new RecentSearchesSection(
      {
        recent_searches: [],
        is_mobile: option.is_mobile,
      },
      {
        klass: {
          container: klass.recent_search_section,
          hide: klass.hide,
        },
        container_type: 'wrap',
        link_type: 'event',
      },
    );

    this.SearchModuleController = new SearchModuleController(this);
  }

  override template() {
    const _klass = this.option.without_visible_interaction
      ? util_klass.visible
      : `${util_klass.hidden} ${util_klass.hiding}`;
    const is_mobile = this.option.is_mobile;

    return html`<div class="${klass.search_module} ${_klass} ${this.option.klass}">
      <div class="${klass.input_section}">
        <div class="${klass.search_input_container}">${this.InputSearchView}</div>
        ${htmlIf(html`<span class="${klass.close_icon}">${CloseIcon()}</span>`, is_mobile)}
      </div>
      <div class="${klass.search_section}">
        <!-- 자동 완성 -->
        ${this.AutoCompleteListView}
        <!-- 최근 검색어 -->
        ${this.RecentSearchesSectionView}
      </div>
    </div>`;
  }

  override async onRender() {
    // FIXME: api로 래핑
    const [{ data: recents }, auto_completes] = await Promise.all([
      axios.get<{ queries: string[] }>(`/${ET.lang}/@api/keywords/history`),
      this.#onRequestAutoComplete(this.data.value),
    ]);

    this.AutoCompleteListView.setData({ auto_completes, value: this.data.value ?? '' });
    this.RecentSearchesSectionView.setData({
      recent_searches: recents.queries.map((search) => ({ name: search })),
    });
    this.redraw();
    this.#changeSearchType(auto_completes.length ? 'auto-complete' : 'recent');
  }

  override onMount() {
    this.delegate(SearchInputEvent, InputSearch, (e) => {
      this.#setValue(e.detail);

      // 자동완성이 가능한 경우는 `SearchDebounceInputEvent` 에서 처리하므로 즉시 종료
      if (this.AutoCompleteController.canAutoComplete()) {
        return;
      }
      // 이전 호출 있다면 취소
      this.AutoCompleteController.cancelPrevRequest();
      // 자동완성 안 되는 경우는 자동완성 목록 가림
      this.#changeSearchType('recent');
    });

    this.delegate(SearchDebounceInputEvent, InputSearch, async () => {
      const auto_completes = await this.AutoCompleteController.handleAutoComplete();
      if (auto_completes) {
        this.#handleChange(auto_completes, this.AutoCompleteController.getSearch());
      }
    });

    this.delegate(AutoCompleteClickEvent, AutoCompleteList, (e) => {
      const search = e.detail;
      if (!search) {
        return;
      }

      this.#changeSearchType('recent');

      this.#setValue(e.detail);
      this.InputSearchView.setValue(search);
      postSearchLogApi({ search, is_popular_search_word: false });
      return this.confirmSearch(search);
    });

    this.delegate(RecentSearchClickEvent, RecentSearchesSection, (e) => {
      const search = e.detail;
      if (!search) {
        return;
      }

      this.#changeSearchType('recent');

      this.#setValue(e.detail);
      this.InputSearchView.setValue(search);
      return this.confirmSearch(search);
    });

    // submit
    this.delegate(SearchSubmitEvent, InputSearch, async (e) => {
      if (!e.detail) {
        this.SearchModuleController.shake();
        return;
      }

      // 검색어 자동완성 즉시 숨김
      this.#changeSearchType('recent');
      // 이전 호출 있다면 취소
      this.AutoCompleteController.cancelPrevRequest();

      let search: string = e.detail;
      const focused_el = this.AutoCompleteListView.getFocusedEl();
      if (focused_el?.dataset?.search) {
        search = focused_el.dataset.search;
      }

      this.#setValue(e.detail);
      this.InputSearchView.setValue(search);
      postSearchLogApi({ search, is_popular_search_word: false });
      await this.confirmSearch(search);
    });

    // key up/down event
    this.delegate(SearchArrowKeydownEvent, InputSearch, (e) => {
      const key = e.detail;
      if (key === 'ArrowUp') {
        this.AutoCompleteListView.moveFocusUp();
      } else if (key === 'ArrowDown') {
        this.AutoCompleteListView.moveFocusDown();
      }
    });

    this.SearchModuleController.onMount();

    // mobile only
    if (this.option.is_mobile) {
      this.delegate('click', `.${klass.close_icon}`, () => {
        this.dispatchEvent(SearchModuleCreatorShopCloseEvent, {
          bubbles: true,
          detail: this.InputSearchView.getValue(),
        });
      });
    }
  }

  open() {
    this.SearchModuleController.open();
    this.InputSearchView.focus();
  }

  close(): string {
    this.SearchModuleController.close();
    return this.InputSearchView.getValue();
  }

  // 검색어 최근 검색어에 추가하고, 결과로 이동
  private async confirmSearch(search: string) {
    const url =
      this.option.search_type === 'ALL'
        ? generateSearchUrl(search)
        : generateCreatorShopSearchUrl(search, this.option.store_domain_name);
    this.InputSearchView.setValue(search);
    await this.SearchModuleController.saveRecentSearch(search);
    location.href = url;
  }

  async changeSearchType(search_type: SearchModuleCreatorShopOption['search_type']) {
    this.option.search_type = search_type;
    const auto_completes = await this.AutoCompleteController.handleAutoComplete();
    return this.#handleChange(auto_completes ?? [], this.data.value);
  }

  async #onRequestAutoComplete(value?: string) {
    try {
      const res = await SearchApiS.getAutoCompleteListApi({ search: value, store_id: this.#getStoreId() });
      return res.auto_completes;
    } catch (e) {
      return [];
    }
  }

  #setValue(value?: string) {
    this.data.value = value;
    this.AutoCompleteController.setSearch(value);
  }

  #getStoreId() {
    if (this.option.search_type === 'CREATOR_SHOP' && this.option.store_id) {
      return this.option.store_id;
    }
  }

  #handleChange(auto_completes: AutoComplete[], value?: string) {
    this.AutoCompleteListView.setData({ auto_completes, value: value ?? '' });
    this.AutoCompleteListView.redraw();
    this.#changeSearchType(auto_completes.length ? 'auto-complete' : 'recent');
  }

  #changeSearchType(type: 'auto-complete' | 'recent') {
    if (type === 'recent') {
      this.AutoCompleteListView.hide();
      this.RecentSearchesSectionView.show();
    } else {
      this.AutoCompleteListView.show();
      this.RecentSearchesSectionView.hide();
    }

    $(this.element()).find(`.${klass.search_section}`)?.setAttribute('data-type', type);
  }
}
